import { inject } from '@angular/core';
import { Router, type ActivatedRouteSnapshot, type CanActivateFn } from '@angular/router';
import { toObservable } from '@angular/core/rxjs-interop';
import { filter, map, take } from 'rxjs';
import { FeatureAccessClientService } from './feature-access-client.service';

/**
 * Blocks routes of unlicensed features (route `data: { feature: '<key>' }`).
 * Waits for the features resource to reach a terminal status. `idle` would
 * otherwise slip through synchronously on subscribe (rxResource emits `idle`
 * before transitioning to `loading`) and let a request fire before the
 * feature answer is known. On `error`, `hasFeature` still fails open — the
 * server-side FeatureGuard is the real enforcement.
 *
 * `data.feature` may be a fine-grained FeatureKey (`'timetracking.clock-in'`)
 * or a coarse ModuleKey (`'timetracking'`) — {@link FeatureAccessClientService.hasFeature}
 * handles both.
 */
export const featureGuard: CanActivateFn = (route: ActivatedRouteSnapshot) => {
  const featureAccess = inject(FeatureAccessClientService);
  const router = inject(Router);
  const feature = route.data?.['feature'] as string | undefined;
  if (!feature) {
    return true;
  }

  if (featureAccess.featuresLoaded()) {
    return featureAccess.hasFeature(feature) ? true : router.createUrlTree(['/dashboard']);
  }

  featureAccess.refresh();

  return toObservable(featureAccess.featuresResource.status).pipe(
    filter(status => status === 'resolved' || status === 'error' || status === 'local'),
    take(1),
    map(status => (status === 'error' || featureAccess.hasFeature(feature) ? true : router.createUrlTree(['/dashboard'])))
  );
};
